import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { resolveImageUrl } from '@/context/FeaturedImagesContext';
import { FeaturedFallback } from '@/components/FeaturedImage';
import { cn } from '@/lib/utils';

/**
 * GalleryStrip — renders N tiles using images from the gallery (by category).
 * If gallery has fewer items than `limit` (or zero), missing tiles render the
 * brand gradient fallback. Never references third-party stock images.
 *
 * Props:
 *   category:    optional gallery category (atelier|matrimoni|bouquet|eventi|workshop|piante|giardini).
 *                If omitted: pulls latest items across categories.
 *   limit:       number of tiles to render (default 4).
 *   random:      if true (default), the strip fetches a fresh random sample from
 *                the requested category every time the component mounts. If false,
 *                the API returns the latest items in curated order.
 *   tileClass:   classes for each tile container (aspect/rounded/etc.).
 *   className:   wrapper grid classes.
 *   ariaLabel:   group aria-label for the strip.
 *   offsetEven:  if true, applies mt-6 to even-index tiles (visual rhythm).
 */
export const GalleryStrip = ({
    category,
    limit = 4,
    random = true,
    tileClass = 'rounded-lg aspect-square',
    className = 'grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4',
    ariaLabel = 'Galleria',
    offsetEven = false,
}) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const params = { limit };
                if (category) params.category = category;
                if (random) params.random = true;
                const res = await api.get('/gallery', { params });
                if (!cancelled) setItems(res.data || []);
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('[GalleryStrip] Failed to load strip items:', err);
                }
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [category, limit, random]);

    // Build N tiles (image when available, otherwise fallback)
    const tiles = Array.from({ length: limit }, (_, i) => items[i] || null);

    return (
        <div className={className} aria-label={ariaLabel} aria-busy={loading || undefined}>
            {tiles.map((it, i) => (
                <div
                    key={it?.id || `placeholder-${i}`}
                    className={cn(
                        'relative overflow-hidden bg-muted',
                        tileClass,
                        offsetEven && i % 2 === 1 ? 'mt-6' : '',
                    )}
                >
                    {it ? (
                        <img
                            src={resolveImageUrl(it.image_url)}
                            alt={it.title || 'Galleria Floreal'}
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <FeaturedFallback label="Floreal" />
                    )}
                </div>
            ))}
        </div>
    );
};

export default GalleryStrip;
